import { useContext, useEffect, useReducer } from 'react'
import {
    Typography,
    TextField,
    Button,
    IconButton,
    Tooltip,
    Card,
    Avatar,
    Box,
    Tab,
    Dialog,
    DialogContent,
    DialogContentText,
    styled,
} from '@mui/material'
import {
    UniversalModal,
    PDFViewer,
    FormSection,
    SaveButton,
    UniversalReactSorter,
    Grid,
    UniversalAutoComplete as AutoComplete,
} from '../../../UniversalComponents'
import {
    addBoatModel,
    getBoatModels,
    getManufacturers,
    getSignalProfiles,
} from '../../../../apiRoutes'
import {
    DriveFolderUpload as DriveFolderUploadIcon,
    PictureAsPdf as PictureAsPdfIcon,
    AddCircle as AddCircleIcon,
    DeleteForever as DeleteForeverIcon,
    CheckCircle as CheckCircleIcon,
    HelpOutline as HelpOutlineIcon,
} from '@mui/icons-material'

import cloneDeep from 'lodash/cloneDeep'
import responseError from '../../../../context/responseError/responseError'
import BoatModelDefaultIconCard from './BoatModelDefaultIcon'
import { ToastMessage } from '../../../../context/ToastPopUpContext'
import { Authentication } from '../../../../context/AuthenticationContext'
import { parseFormWithIndentifierBoatModels } from '../../../../utilities/functionTools'

const BoatModelForm = ({
    row,
    formDescription,
    saveCache,
    onClose,
    permissionCheck,
    quickStart,
    defaultIcons,
}) => {
    const currentUser = useContext(Authentication)
    const { activeManufacturer } = currentUser.auth
    const defaultState = {
        formData: {
            manufacturer: row?.manufacturer?.manufacturerName || '',
            modelYear: row?.modelYear || '',
            model: row?.model || '',
            modelImageUrl: row?.modelImageUrl || '',
            modelOwnersManualUrl: row?.modelOwnersManualUrl || '',
            id: row?.id ? row?.id : '',
            signalProfile: row?.signalProfile || { profileName: '' },
            boatIconUrl: row.boatIconUrl || '',
            ownersResources: cloneDeep(row?.ownersResources || []),
        },
        formValidation: {
            manufacturerValidation: false,
            modelYearValidation: false,
            modelValidation: false,
            signalProfileValidation: false,
            isFormDirty: false,
            isFormValid: false,
            resourceTypeValidation: false,
            resourceValidation: false,
            descriptionValidation: false,
            iconValidation: false,
            titleValidation: false,
        },
        modelImageFile: {},
        modelImage: '',
        modelOwnerManualFileType: '',
        modelOwnerManualFile: {},
        modelOwnerManualFileName: '',
        manufacturerOptions: [],
        manufacturers: [],
        permissionsCheck: permissionCheck,
        openManual: false,
        signalProfilesList: [],
        signalProfiles: [],
        clonedResources: '',
        imagePreview: '',
        selectedIndex: null,
        openResourcePreview: false,
        ownersResourceFileName: '',
        ownerResourceOptions: [],
        ownerResources: [],
        testOwners: [],
        openOwnersHelp: false,
    }

    const reducer = (state, newState) => ({ ...state, ...newState })

    const [state, setState] = useReducer(reducer, defaultState)
    const setToastMessage = useContext(ToastMessage)

    const {
        formData,
        formValidation,
        manufacturers,
        manufacturerOptions,
        permissionsCheck,
        modelImage,
        modelImageFile,
        modelOwnerManualFile,
        modelOwnerManualFileType,
        modelOwnerManualFileName,
        openManual,
        signalProfilesList,
        signalProfiles,
        icon,
        clonedResources,
        imagePreview,
        selectedIndex,
        openResourcePreview,
        ownersResourceFileName,
        ownerResourceOptions,
        ownerResources,
        openOwnersHelp,
    } = state

    const {
        manufacturerValidation,
        modelYearValidation,
        modelValidation,
        signalProfileValidation,
        resourceTypeValidation,
        resourceValidation,
        descriptionValidation,
        iconValidation,
        titleValidation,
        isFormDirty,
        isFormValid,
    } = formValidation

    const {
        manufacturer,
        model,
        modelYear,
        modelImageUrl,
        modelOwnersManualUrl,
        signalProfile,
        boatIconUrl,
        ownersResources,
    } = formData

    const handleChange = (e: any) => {
        const { name, value } = e.target

        if (name === 'signalProfile') {
            const selectProfile = signalProfiles.find(
                ({ profileName }) => profileName === value
            )
            setState({
                formData: {
                    ...formData,
                    signalProfile: selectProfile,
                },
                formValidation: {
                    ...formValidation,
                    isFormDirty: true,
                },
            })
        } else if (name === 'boatIconUrl') {
            setState({
                formData: {
                    ...formData,
                    [name]: value,
                    modelImageUrl: value,
                },
                formValidation: {
                    ...formValidation,
                    isFormDirty: true,
                },
                icon: '',
                selectedFiles: [],
            })
        } else {
            setState({
                formData: { ...formData, [name]: value },
            })
        }
        saveCache({ ...formData, [name]: value })
    }

    const validateOnBlur = (e: any) => {
        const { name, value } = e.target

        if (value !== '') {
            setState({
                formValidation: {
                    ...formValidation,
                    [name + 'Validation']: false,
                    isFormDirty: true,
                },
            })
        } else {
            setState({
                formValidation: {
                    ...formValidation,
                    [name + 'Validation']: true,
                    isFormDirty: true,
                },
            })
        }
    }

    const handleSubmit = async () => {
        try {
            const findManufacturer = manufacturers.find(
                ({ manufacturerName }) => manufacturerName === manufacturer
            )

            let res: any
            let selectedFiles: any = []
            const newFormData = {
                ...formData,
                manufacturer: findManufacturer?._id,
                signalProfile: signalProfile?._id,
            }

            selectedFiles = [modelImageFile, modelOwnerManualFile]
            ownersResources?.forEach((resource) => {
                if (resource?.file) selectedFiles.push(resource?.file)
                if (resource?.image) selectedFiles.push(resource?.image)
            })

            const parsedFormData: any = parseFormWithIndentifierBoatModels(
                newFormData,
                selectedFiles
            )

            res = await addBoatModel(parsedFormData)

            if (res) {
                const { data, status } = res
                if (data && status === 200) {
                    setToastMessage(
                        `Boat Model: ${data.model} was ${
                            row.id ? 'edited' : 'added'
                        } successfully`,
                        'success'
                    )

                    onClose(row.model, data)
                } else {
                    responseError(res, row)
                }
            }
        } catch (err: any) {
            console.error(err, 'error')
            setToastMessage(`${err}`, 'error')
        }
    }

    const handleReset = () =>
        setState({
            formData: defaultState.formData,
            formValidation: defaultState.formValidation,
            modelImage: defaultState.modelImage,
            modelImageFile: defaultState.modelImageFile,
            modelOwnerManualFile: defaultState.modelOwnerManualFile,
            modelOwnerManualFileName: defaultState.modelOwnerManualFileName,
            imagePreview: defaultState.imagePreview,
            ownersResourceFileName: defaultState.ownersResourceFileName,
            selectedIndex: defaultState.selectedIndex,
        })

    const handleFileSelect = (event: any, image?: boolean) => {
        if (event.target.files && event.target.files.length !== 0) {
            const file = event.target.files[0]

            const reader = (file, callback) => {
                const fr = new FileReader()
                fr.onload = () => callback(null, fr.result)
                fr.onerror = (err) => callback(err)
                fr.readAsDataURL(file)
            }

            if (image) {
                reader(file, (err, res) => {
                    setState({
                        modelImage: res,
                        modelImageFile: file,
                        formValidation: {
                            ...formValidation,
                            isFormDirty: true,
                        },
                    })
                })
            } else {
                setState({
                    modelOwnerManualFile: file,
                    modelOwnerManualFileName: file.name,
                    modelOwnerManualFileType: file.type,
                    formValidation: { ...formValidation, isFormDirty: true },
                })
            }
        }
    }

    const handleOwnersResourcesFileSelect = (
        event: any,
        index: number,
        image?: boolean
    ) => {
        if (event.target.files && event.target.files.length !== 0) {
            const file = event.target.files[0]
            ;(file as any).tag = `ownersResources${index}`

            // Create a copy of the ownersResources array
            const updatedResources = [...(ownersResources || [])]

            // Update the resource at the given index

            const reader = (file, callback) => {
                const fr = new FileReader()
                fr.onload = () => callback(null, fr.result)
                fr.onerror = (err) => callback(err)
                fr.readAsDataURL(file)
            }

            if (image) {
                reader(file, (err, res) => {
                    updatedResources[index] = {
                        ...updatedResources[index],
                        image: file,
                        imagePreview: res,
                    }
                    setState({
                        formData: {
                            ...formData,
                            ownersResources: updatedResources,
                        },
                        formValidation: {
                            ...formValidation,
                            isFormDirty: true,
                        },
                    })
                })
            } else {
                updatedResources[index] = {
                    ...updatedResources[index],
                    file,
                    ownersResourceFileName: file?.name,
                }

                setState({
                    formData: {
                        ...formData,
                        ownersResources: updatedResources,
                    },

                    formValidation: {
                        ...formValidation,
                        isFormDirty: true,
                    },
                })
            }
        }
    }

    const getData = async () => {
        let manufacturerOptions: any = []
        let manufacturers: any = []
        let signalProfiles: any = []
        let signalProfilesList: any = []
        let ownerResources: any = []
        let ownerResourceOptions: any = []

        try {
            const getManufacturersData = await getManufacturers(true)
            const getSignalProfileData = await getSignalProfiles(true)
            const getOwnerResourcesData = await getBoatModels(true)

            if (getManufacturersData && getManufacturersData.length !== 0) {
                manufacturers = getManufacturersData.filter(
                    (manufacturer) => manufacturer.manufacturerType === 'boat'
                )

                manufacturerOptions = manufacturers.map(
                    ({ manufacturerName }) => manufacturerName
                )
            }
            if (getSignalProfileData && getSignalProfileData.length !== 0) {
                signalProfiles = getSignalProfileData
                signalProfilesList = getSignalProfileData.map(
                    ({ profileName }) => profileName
                )
            }
            if (getOwnerResourcesData && getOwnerResourcesData.length !== 0) {
                ownerResources = {}

                getOwnerResourcesData.forEach(({ model, ownersResources }) => {
                    if (
                        Array.isArray(ownersResources) &&
                        ownersResources.length > 0
                    ) {
                        ownerResources[model] = ownersResources
                    }
                })
                if (
                    ownerResources &&
                    Object.keys(ownerResources).length !== 0
                ) {
                    ownerResourceOptions = Object.keys(ownerResources).filter(
                        (model) => model !== row?.model
                    )
                }
            }
            setState({
                manufacturers,
                manufacturerOptions,
                signalProfiles,
                signalProfilesList,
                ownerResourceOptions,
                ownerResources,
            })
        } catch (err: any) {
            setToastMessage(err, 'error')
            console.error(err, 'error')
        }
    }

    // this code below is for filtering signal profiles by manufacturers when it needs to be implemented.

    // const filterProfilesByManufacturerId = (manufacturer) => {
    //     if (manufacturer) {
    //         const findManufacturer = manufacturers.find(
    //             ({ manufacturerName }) => manufacturerName === manufacturer
    //         )

    //         const filteredModels = signalProfiles
    //             .filter(
    //                 (model) => model?.manufacturer._id === findManufacturer?._id
    //             )
    //             .map((model) => model.profileName)

    //         return filteredModels
    //     } else {
    //         return []
    //     }
    // }

    // useEffect(() => {
    //     const findSignalProfiles = filterProfilesByManufacturerId(manufacturer)

    //     if (findSignalProfiles) {
    //         setState({ signalProfilesList: findSignalProfiles })
    //     }
    // }, [manufacturer])

    useEffect(() => {
        getData()
    }, [])

    const VisuallyHiddenInput = styled('input')({
        clip: 'rect(0 0 0 0)',
        clipPath: 'inset(50%)',
        height: 1,
        overflow: 'hidden',
        position: 'absolute',
        bottom: 0,
        left: 0,
        whiteSpace: 'nowrap',
        width: 1,
    })

    const selectedResource =
        selectedIndex !== null ? ownersResources[selectedIndex] : null

    const resourceTypeOption = ['URL', 'PDF']

    useEffect(() => {
        if (
            isFormDirty &&
            !manufacturerValidation &&
            !modelYearValidation &&
            !modelValidation &&
            !signalProfileValidation &&
            manufacturer &&
            model &&
            modelYear &&
            signalProfile.profileName
        ) {
            setState({
                formValidation: {
                    ...formValidation,
                    isFormValid: true,
                },
            })
        } else {
            setState({
                formValidation: {
                    ...formValidation,
                    isFormValid: false,
                },
            })
        }
    }, [
        isFormDirty,
        manufacturerValidation,
        modelYearValidation,
        modelValidation,
        signalProfileValidation,
        manufacturer,
        model,
        modelYear,
        signalProfile.profileName,
    ])

    useEffect(() => {
        const urlValid = () => {
            if (selectedResource?.resourceType === 'URL') {
                if (selectedResource?.resource && !resourceValidation) {
                    const urlRegex =
                        /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w\-._~:/?#[\]@!$&'()*+,;=]*)?$/i
                    return urlRegex.test(selectedResource?.resource)
                }
            } else {
                if (
                    selectedResource &&
                    (selectedResource?.file ||
                        selectedResource?.resource !== '')
                ) {
                    return true
                } else {
                    return false
                }
            }
        }
        const isIconValid = () => {
            if (
                selectedResource &&
                (selectedResource?.image || selectedResource?.icon !== '')
            ) {
                return true
            } else {
                return false
            }
        }
        if (
            isFormDirty &&
            !resourceTypeValidation &&
            !descriptionValidation &&
            !titleValidation &&
            urlValid() &&
            isIconValid() &&
            selectedResource?.resourceType &&
            selectedResource?.title &&
            selectedResource?.description
        ) {
            setState({
                formValidation: {
                    ...formValidation,
                    isFormValid: true,
                },
            })
        } else {
            if (selectedIndex === null && selectedResource === null) {
                setState({
                    formValidation: {
                        ...formValidation,
                        isFormValid: true,
                    },
                })
            } else {
                setState({
                    formValidation: {
                        ...formValidation,
                        isFormValid: false,
                    },
                })
            }
        }
    }, [
        resourceTypeValidation,
        resourceValidation,
        descriptionValidation,
        selectedResource?.resourceType,
        selectedResource?.resource,
        selectedResource?.title,
        selectedResource?.description,
        selectedResource?.file,
        selectedResource?.image,
    ])

    useEffect(() => {
        if (activeManufacturer) {
            setState({
                formData: {
                    ...formData,
                    manufacturer: activeManufacturer,
                },
            })
        }
    }, [])

    const handleResourceChange = (index: any, e: any) => {
        const { name, value } = e.target
        const updated = [...ownersResources]
        updated[index][name] = value

        setState({ ownersResources: updated })
    }

    const handleRemoveResource = (indexToRemove) => {
        const updatedResources = ownersResources.filter(
            (_resource, index) => index !== indexToRemove
        )
        setState({
            formData: {
                ...formData,
                ownersResources: updatedResources,
            },
        })
        if (updatedResources.length === 0) {
            setState({ selectedIndex: null })
        } else if (indexToRemove >= updatedResources.length) {
            setState({ selectedIndex: updatedResources.length - 1 })
        } else {
            setState({ selectedIndex: indexToRemove })
        }
    }
    const handleClonningResources = (e: any) => {
        const { value } = e.target

        if (value && ownerResources && Object.keys(ownerResources).length > 0) {
            const clonedResources = ownerResources[value]

            if (Array.isArray(clonedResources)) {
                const filteredClones = clonedResources.filter((newResource) => {
                    return !ownersResources.some((existingResource) => {
                        return (
                            existingResource?.resource === newResource?.resource
                        )
                    })
                })

                if (filteredClones.length > 0) {
                    setState({
                        formData: {
                            ...formData,
                            ownersResources: [
                                ...ownersResources,
                                ...filteredClones,
                            ],
                        },
                        formValidation: {
                            ...formValidation,
                            isFormDirty: true,
                        },
                    })
                }
            }
        }
    }

    return (
        <Grid container spacing={2}>
            <Grid item xs={12} md={6} sx={{ marginTop: 6 }}>
                <Grid container spacing={2}>
                    <FormSection title="Boat Model Details">
                        <Grid item xs={12} md={12}>
                            <AutoComplete
                                required
                                options={manufacturerOptions}
                                label="Boat Manufacturer"
                                name="manufacturer"
                                handleChange={handleChange}
                                value={manufacturer}
                                onBlur={validateOnBlur}
                                error={manufacturerValidation && !manufacturer}
                                helperText={
                                    manufacturerValidation && !manufacturer
                                        ? 'Please select a Boat Manufacturer name.'
                                        : ''
                                }
                                clearFunction={
                                    permissionsCheck
                                        ? false
                                        : () =>
                                              setState({
                                                  formData: {
                                                      ...formData,
                                                      manufacturer: '',
                                                  },
                                              })
                                }
                                disabled={true}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                required={true}
                                label="Model Year"
                                name="modelYear"
                                fullWidth
                                onChange={handleChange}
                                value={modelYear}
                                onBlur={validateOnBlur}
                                error={modelYearValidation && !modelYear}
                                helperText={
                                    modelYearValidation && !modelYear
                                        ? 'Please enter a Enter a Model Year.'
                                        : ''
                                }
                                disabled={permissionsCheck}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                required={true}
                                label="Model"
                                name="model"
                                fullWidth
                                onChange={handleChange}
                                value={model}
                                onBlur={validateOnBlur}
                                error={modelValidation && !model}
                                helperText={
                                    modelValidation && !model
                                        ? 'Please enter a Enter a Model.'
                                        : ''
                                }
                                disabled={permissionsCheck}
                            />
                        </Grid>
                    </FormSection>
                </Grid>
            </Grid>
            <Grid item xs={12} md={6} sx={{ marginTop: 6 }}>
                <FormSection
                    title="Signal Profile Selector"
                    titleAlignment="left"
                >
                    <Grid item xs={12} md={12}>
                        <AutoComplete
                            required
                            options={signalProfilesList}
                            label="Signal Profile"
                            name="signalProfile"
                            handleChange={handleChange}
                            value={signalProfile.profileName}
                            clearFunction={
                                permissionsCheck
                                    ? false
                                    : () =>
                                          setState({
                                              formData: {
                                                  ...formData,
                                                  signalProfile: {
                                                      profileName: '',
                                                  },
                                              },
                                          })
                            }
                            error={
                                signalProfileValidation &&
                                !signalProfile.profileName
                            }
                            helperText={
                                signalProfileValidation &&
                                !signalProfile.profileName
                                    ? 'Please enter a Enter a Signal Profile.'
                                    : ''
                            }
                            disabled={permissionsCheck}
                        />
                    </Grid>
                </FormSection>
            </Grid>

            <Grid item xs={12} md={12}>
                <Grid item xs={12} md={12}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={12}>
                            <FormSection
                                title="Boat Model Image"
                                titleAlignment="center"
                            >
                                <Grid item xs={12}>
                                    <label htmlFor="raised-button-file">
                                        <Tooltip
                                            title={
                                                boatIconUrl
                                                    ? 'Replace Default Icon'
                                                    : 'Upload Icon'
                                            }
                                        >
                                            <center>
                                                {boatIconUrl ||
                                                icon ||
                                                modelImage ||
                                                modelImageUrl ? (
                                                    <IconButton component="label">
                                                        <Box>
                                                            <Avatar
                                                                src={
                                                                    icon ||
                                                                    modelImage ||
                                                                    boatIconUrl ||
                                                                    modelImageUrl
                                                                }
                                                                sx={{
                                                                    width: 100,
                                                                    height: 100,
                                                                }}
                                                            />
                                                            <VisuallyHiddenInput
                                                                onChange={(
                                                                    event
                                                                ) =>
                                                                    handleFileSelect(
                                                                        event,
                                                                        true
                                                                    )
                                                                }
                                                                type="file"
                                                                accept="image/*"
                                                                id='htmlFor="raised-button-file'
                                                                disabled={
                                                                    permissionCheck
                                                                }
                                                            />
                                                        </Box>
                                                    </IconButton>
                                                ) : (
                                                    <IconButton component="label">
                                                        <DriveFolderUploadIcon
                                                            sx={{
                                                                fontSize: 40,
                                                            }}
                                                        />
                                                        <VisuallyHiddenInput
                                                            onChange={(event) =>
                                                                handleFileSelect(
                                                                    event,
                                                                    true
                                                                )
                                                            }
                                                            type="file"
                                                            accept="image/*"
                                                            id='htmlFor="raised-button-file'
                                                            disabled={
                                                                permissionCheck
                                                            }
                                                        />
                                                    </IconButton>
                                                )}
                                            </center>
                                        </Tooltip>
                                    </label>
                                </Grid>
                            </FormSection>
                        </Grid>
                    </Grid>
                </Grid>
                {defaultIcons && defaultIcons?.length !== 0 && (
                    <Grid item xs={12} sx={{ marginTop: 3 }}>
                        <FormSection
                            title="Select a Default Icon"
                            titleAlignment="left"
                        >
                            {defaultIcons
                                .filter(
                                    (icon, index, self) =>
                                        index ===
                                        self.findIndex(
                                            (i) => i.name === icon.name
                                        )
                                )
                                .map((defaultIcon: any) => (
                                    <BoatModelDefaultIconCard
                                        cdnUrl={defaultIcon?.cdnUrl}
                                        name={defaultIcon?.name}
                                        boatIconUrl={boatIconUrl}
                                        handleChange={handleChange}
                                        key={defaultIcon?._id}
                                        icon={icon}
                                    />
                                ))}
                        </FormSection>
                    </Grid>
                )}
            </Grid>
            <Grid item xs={12} md={12}>
                <FormSection
                    title={
                        row?.model
                            ? `Owner Resources for ${model}`
                            : 'Add Owner Resources'
                    }
                >
                    <Grid item xs={12} md={12}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={12}>
                                <Typography
                                    variant="h4"
                                    align="center"
                                    color="primary"
                                >
                                    Configure Owner Resources Below
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={12}>
                                <Grid container spacing={2}>
                                    <Grid item xs={10} md={7}>
                                        <AutoComplete
                                            options={ownerResourceOptions}
                                            handleChange={
                                                handleClonningResources
                                            }
                                            value={clonedResources}
                                            name="clonnedResources"
                                            label="Clone Owner Resources"
                                        />
                                    </Grid>
                                    <Grid item xs={1} md={1}>
                                        <Tooltip title="Clone Owner Resource Help">
                                            <IconButton
                                                onClick={() => {
                                                    setState({
                                                        openOwnersHelp: true,
                                                    })
                                                }}
                                            >
                                                <HelpOutlineIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </Grid>
                                    <Grid item xs={2} md={4}>
                                        <Button
                                            startIcon={<AddCircleIcon />}
                                            variant="contained"
                                            sx={{
                                                width: '100%',
                                                height: 55,
                                            }}
                                            onClick={() => {
                                                const newResource = {
                                                    resourceType: '',
                                                    description: '',
                                                    resource: '',
                                                    icon: '',
                                                    title: '',
                                                }
                                                const newIndex =
                                                    ownersResources.length

                                                const updatedResources = [
                                                    ...ownersResources,
                                                    newResource,
                                                ]
                                                setState({
                                                    formData: {
                                                        ...formData,
                                                        ownersResources:
                                                            updatedResources,
                                                    },
                                                    selectedIndex: newIndex,
                                                    formValidation: {
                                                        ...formValidation,
                                                        isFormDirty: true,
                                                    },
                                                })
                                            }}
                                        >
                                            Add New Owner Resource
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12} md={12}>
                                <Grid item xs={12} md={12}>
                                    <UniversalReactSorter
                                        list={ownersResources}
                                        disabled={permissionCheck}
                                        key={selectedIndex}
                                        group="fixed"
                                        setList={(drag) =>
                                            setState({
                                                formData: {
                                                    ...formData,
                                                    ownersResources: drag,
                                                },
                                                formValidation: {
                                                    ...formValidation,
                                                    isFormDirty: true,
                                                },
                                            })
                                        }
                                    >
                                        {ownersResources &&
                                            ownersResources.map(
                                                (resource, index) => (
                                                    <Tab
                                                        label={resource?.title}
                                                        key={index}
                                                        wrapped
                                                        onClick={() =>
                                                            setState({
                                                                selectedIndex:
                                                                    index,
                                                            })
                                                        }
                                                        value={index}
                                                        iconPosition="start"
                                                        sx={{
                                                            borderBottom:
                                                                index ===
                                                                selectedIndex
                                                                    ? 2
                                                                    : 0,
                                                            borderBottomColor:
                                                                index ===
                                                                selectedIndex
                                                                    ? '#8cc63f'
                                                                    : 'black',
                                                        }}
                                                        icon={
                                                            <Avatar
                                                                src={
                                                                    resource?.icon
                                                                }
                                                            />
                                                        }
                                                    />
                                                )
                                            )}
                                    </UniversalReactSorter>
                                </Grid>
                            </Grid>
                            <Grid item xs={12} md={12}>
                                {selectedResource && (
                                    <FormSection
                                        sx={{
                                            marginTop: 5,
                                            border: '1px',
                                            borderRadius: 5,
                                            backgroundColor: '#333',
                                        }}
                                        titleBottom={'All Fields are Required'}
                                        titleBottomAlignment="center"
                                        reset={
                                            <Tooltip title="Delete">
                                                <IconButton
                                                    key={selectedIndex}
                                                    onClick={() => {
                                                        handleRemoveResource(
                                                            selectedIndex
                                                        )
                                                        setState({
                                                            formValidation: {
                                                                ...formValidation,
                                                                isFormDirty:
                                                                    true,
                                                            },
                                                        })
                                                    }}
                                                >
                                                    <DeleteForeverIcon />
                                                </IconButton>
                                            </Tooltip>
                                        }
                                    >
                                        <Grid item xs={12} md={12}>
                                            <Grid
                                                container
                                                spacing={2}
                                                sx={{ padding: 2 }}
                                            >
                                                <Grid item md={8}>
                                                    <Grid container spacing={2}>
                                                        <Grid
                                                            item
                                                            xs={12}
                                                            md={4}
                                                        >
                                                            <FormSection
                                                                title="Upload Icon"
                                                                titleAlignment="center"
                                                            >
                                                                <Grid
                                                                    item
                                                                    md={12}
                                                                >
                                                                    <center>
                                                                        <Button component="label">
                                                                            <Avatar
                                                                                src={
                                                                                    selectedResource?.imagePreview
                                                                                        ? selectedResource?.imagePreview
                                                                                        : selectedResource?.icon
                                                                                }
                                                                                sx={{
                                                                                    width: 100,
                                                                                    height: 100,
                                                                                }}
                                                                            />

                                                                            <VisuallyHiddenInput
                                                                                onChange={(
                                                                                    event
                                                                                ) =>
                                                                                    handleOwnersResourcesFileSelect(
                                                                                        event,
                                                                                        selectedIndex,
                                                                                        true
                                                                                    )
                                                                                }
                                                                                type="file"
                                                                                accept="image/*"
                                                                                id='htmlFor="raised-button-file'
                                                                                disabled={
                                                                                    permissionCheck
                                                                                }
                                                                            />
                                                                        </Button>
                                                                    </center>
                                                                </Grid>
                                                            </FormSection>
                                                        </Grid>
                                                        <Grid
                                                            item
                                                            xs={2}
                                                            md={6}
                                                        >
                                                            <Grid
                                                                container
                                                                spacing={2}
                                                            >
                                                                <Grid
                                                                    item
                                                                    xs={4}
                                                                    md={12}
                                                                >
                                                                    <AutoComplete
                                                                        key={
                                                                            selectedIndex
                                                                        }
                                                                        options={
                                                                            resourceTypeOption
                                                                        }
                                                                        handleChange={(
                                                                            event
                                                                        ) => {
                                                                            handleResourceChange(
                                                                                selectedIndex,
                                                                                event
                                                                            )
                                                                        }}
                                                                        label={
                                                                            'Resource Type'
                                                                        }
                                                                        name="resourceType"
                                                                        required={
                                                                            true
                                                                        }
                                                                        value={
                                                                            selectedResource?.resourceType
                                                                        }
                                                                        onBlur={
                                                                            validateOnBlur
                                                                        }
                                                                        fullWidth
                                                                        error={
                                                                            resourceTypeValidation &&
                                                                            !selectedResource?.resourceType
                                                                        }
                                                                        helperText={
                                                                            resourceTypeValidation &&
                                                                            !selectedResource?.resourceType
                                                                                ? 'Please enter a resource type'
                                                                                : ''
                                                                        }
                                                                    />
                                                                </Grid>
                                                                <Grid
                                                                    item
                                                                    xs={4}
                                                                    md={12}
                                                                >
                                                                    <TextField
                                                                        key={
                                                                            selectedIndex
                                                                        }
                                                                        onChange={(
                                                                            e
                                                                        ) =>
                                                                            handleResourceChange(
                                                                                selectedIndex,
                                                                                e
                                                                            )
                                                                        }
                                                                        label={
                                                                            'Title'
                                                                        }
                                                                        name="title"
                                                                        required={
                                                                            true
                                                                        }
                                                                        value={
                                                                            selectedResource?.title
                                                                        }
                                                                        onBlur={
                                                                            validateOnBlur
                                                                        }
                                                                        error={
                                                                            titleValidation &&
                                                                            !selectedResource?.title
                                                                        }
                                                                        helperText={
                                                                            titleValidation &&
                                                                            !selectedResource?.title
                                                                                ? 'Please enter a title'
                                                                                : ''
                                                                        }
                                                                        fullWidth
                                                                    />
                                                                </Grid>
                                                                <Grid
                                                                    item
                                                                    xs={4}
                                                                    md={12}
                                                                >
                                                                    <TextField
                                                                        key={
                                                                            selectedIndex
                                                                        }
                                                                        onChange={(
                                                                            e
                                                                        ) =>
                                                                            handleResourceChange(
                                                                                selectedIndex,
                                                                                e
                                                                            )
                                                                        }
                                                                        label={
                                                                            'Description'
                                                                        }
                                                                        name="description"
                                                                        required={
                                                                            true
                                                                        }
                                                                        value={
                                                                            selectedResource?.description
                                                                        }
                                                                        onBlur={
                                                                            validateOnBlur
                                                                        }
                                                                        error={
                                                                            descriptionValidation &&
                                                                            !selectedResource?.description
                                                                        }
                                                                        helperText={
                                                                            descriptionValidation &&
                                                                            !selectedResource?.description
                                                                                ? 'Please enter a description'
                                                                                : ''
                                                                        }
                                                                        fullWidth
                                                                    />
                                                                </Grid>
                                                            </Grid>
                                                        </Grid>

                                                        {selectedResource?.resourceType ===
                                                            'URL' && (
                                                            <Grid
                                                                item
                                                                xs={12}
                                                                md={12}
                                                            >
                                                                <TextField
                                                                    key={
                                                                        selectedResource?.index
                                                                    }
                                                                    onChange={(
                                                                        e
                                                                    ) =>
                                                                        handleResourceChange(
                                                                            selectedIndex,
                                                                            e
                                                                        )
                                                                    }
                                                                    label={
                                                                        'URL'
                                                                    }
                                                                    name="resource"
                                                                    value={
                                                                        selectedResource?.resource
                                                                    }
                                                                    onBlur={
                                                                        validateOnBlur
                                                                    }
                                                                    error={
                                                                        resourceValidation &&
                                                                        !selectedResource?.resource
                                                                    }
                                                                    helperText={
                                                                        resourceValidation &&
                                                                        !selectedResource?.recource
                                                                            ? 'Please enter a resource URL'
                                                                            : ''
                                                                    }
                                                                    required={
                                                                        true
                                                                    }
                                                                    fullWidth
                                                                />
                                                            </Grid>
                                                        )}
                                                    </Grid>
                                                </Grid>
                                                <Grid item md={4}>
                                                    <Grid container spacing={2}>
                                                        <Grid
                                                            item
                                                            xs={4}
                                                            md={10}
                                                        >
                                                            {selectedResource?.resourceType ===
                                                                'PDF' && (
                                                                <Grid
                                                                    container
                                                                    spacing={2}
                                                                >
                                                                    <FormSection
                                                                        titleBottom={
                                                                            selectedResource?.ownersResourceFileName &&
                                                                            selectedResource?.ownersResourceFileName !==
                                                                                '' &&
                                                                            selectedResource?.ownersResourceFileName
                                                                        }
                                                                        title="Upload PDF"
                                                                        titleAlignment="center"
                                                                    >
                                                                        <Grid
                                                                            item
                                                                            xs={
                                                                                12
                                                                            }
                                                                            md={
                                                                                2
                                                                            }
                                                                        >
                                                                            <Tooltip
                                                                                title={
                                                                                    selectedResource?.resource
                                                                                        ? ` Replace ${selectedResource?.resourceType}`
                                                                                        : ` Upload ${selectedResource?.resourceType}`
                                                                                }
                                                                            >
                                                                                <IconButton component="label">
                                                                                    <DriveFolderUploadIcon
                                                                                        sx={{
                                                                                            fontSize: 55,
                                                                                        }}
                                                                                    />
                                                                                    {selectedResource?.ownersResourceFileName &&
                                                                                        selectedResource?.ownersResourceFileName !==
                                                                                            '' && (
                                                                                            <CheckCircleIcon
                                                                                                color="primary"
                                                                                                sx={{
                                                                                                    height: 55,
                                                                                                }}
                                                                                            />
                                                                                        )}
                                                                                    <VisuallyHiddenInput
                                                                                        onChange={(
                                                                                            event
                                                                                        ) =>
                                                                                            handleOwnersResourcesFileSelect(
                                                                                                event,
                                                                                                selectedIndex
                                                                                            )
                                                                                        }
                                                                                        type="file"
                                                                                        accept={
                                                                                            'application/pdf,'
                                                                                        }
                                                                                        id='htmlFor="raised-button-file'
                                                                                        disabled={
                                                                                            permissionCheck
                                                                                        }
                                                                                    />
                                                                                </IconButton>
                                                                            </Tooltip>
                                                                        </Grid>
                                                                        {selectedResource?.resourceType ===
                                                                            'PDF' &&
                                                                            selectedResource?.resource !==
                                                                                '' &&
                                                                            !selectedResource?.ownersResourceFileName && (
                                                                                // for viewing
                                                                                <Grid
                                                                                    item
                                                                                    xs={
                                                                                        12
                                                                                    }
                                                                                    md={
                                                                                        12
                                                                                    }
                                                                                >
                                                                                    <Button
                                                                                        variant="contained"
                                                                                        color="primary"
                                                                                        onClick={() =>
                                                                                            setState(
                                                                                                {
                                                                                                    openResourcePreview:
                                                                                                        true,
                                                                                                }
                                                                                            )
                                                                                        }
                                                                                        disabled={
                                                                                            permissionsCheck
                                                                                        }
                                                                                        sx={{
                                                                                            width: '100%',
                                                                                            height: 55,
                                                                                        }}
                                                                                    >
                                                                                        View{' '}
                                                                                        {
                                                                                            selectedResource?.title
                                                                                        }
                                                                                    </Button>
                                                                                </Grid>
                                                                            )}
                                                                    </FormSection>
                                                                </Grid>
                                                            )}
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </FormSection>
                                )}
                            </Grid>
                        </Grid>
                    </Grid>
                </FormSection>
            </Grid>

            <Grid item xs={12} sx={{ marginTop: -1 }}>
                <SaveButton
                    handleSubmit={handleSubmit || permissionCheck}
                    handleReset={handleReset || permissionCheck}
                    disabledSave={!isFormValid}
                    disabledReset={!isFormDirty}
                    quickStart={quickStart}
                />
            </Grid>
            <UniversalModal
                title="Owners' Manual"
                open={openManual}
                onClose={() => setState({ openManual: false })}
                wrapperStyle={{
                    margin: 'auto',
                    width: '90%',
                    marginTop: 5,
                }}
            >
                <PDFViewer url={modelOwnersManualUrl} />
            </UniversalModal>
            <UniversalModal
                title={selectedResource?.title}
                open={openResourcePreview}
                onClose={() => setState({ openResourcePreview: false })}
                wrapperStyle={{
                    margin: 'auto',
                    width: '90%',
                    marginTop: 5,
                }}
            >
                <PDFViewer
                    key={selectedIndex}
                    url={selectedResource?.resource}
                />
            </UniversalModal>
            <Dialog
                open={openOwnersHelp}
                onClose={() => setState({ openOwnersHelp: false })}
            >
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        <Typography variant="h5" component="div" sx={{ mb: 1 }}>
                            Clone Owner Resources Help
                        </Typography>
                        <ul style={{ paddingLeft: '1.5rem', margin: 0 }}>
                            <li>
                                Selecting a Boat Model from the Clone Owner
                                Resources dropdown will add those Resources to
                                the current Model being edited.
                            </li>
                            <li>Duplicates will not be added.</li>
                        </ul>
                    </DialogContentText>
                </DialogContent>
            </Dialog>
        </Grid>
    )
}

export default BoatModelForm
